import { computed } from "vue";

import { isWebVideoFileUrl } from "shared/helpers/media";
import { type Nullable } from "shared/types";
import {
  type MentionsWithPostAssets,
  MentionType,
} from "shared/types/mentions";

export type PhotoAttachment = {
  type: "photo";
  thumbnail: Nullable<string>;
};

export type VideoAttachment = {
  thumbnail: string;
  type: "video";
  url: Nullable<string>;
};

function defaultThumbnail(mention: MentionsWithPostAssets) {
  if ("thumbnail_url" in mention && mention.thumbnail_url) {
    return mention.thumbnail_url;
  }

  if ("image" in mention && mention.image) {
    return mention.image;
  }

  return "";
}

export function useMentionAttachments(mention: MentionsWithPostAssets) {
  const hasAttachments = computed(() =>
    Boolean(
      (photos.value.length && photos.value.some((item) => item.thumbnail)) ||
        (videos.value.length && videos.value.some((item) => item.url)) ||
        ("video_url" in mention && mention.video_url) ||
        ("thumbnail_url" in mention && mention.thumbnail_url)
    )
  );

  const attachments = computed(() => {
    switch (mention.type) {
      case MentionType.facebook_post:
        return "facebook_post_assets" in mention
          ? mention.facebook_post_assets
          : [];
      case MentionType.instagram_post:
        return "instagram_post_assets" in mention
          ? mention.instagram_post_assets
          : [];
      case MentionType.reddit_post:
        return "reddit_post_assets" in mention
          ? mention.reddit_post_assets
          : [];
      case MentionType.bluesky_post:
        return "assets" in mention ? mention.assets : [];
      case MentionType.tweet:
        return "tweet_assets" in mention ? mention.tweet_assets : [];
      default:
        return [];
    }
  });

  const photos = computed(() => {
    const photoAttachments: PhotoAttachment[] = [];
    const thumbnail = defaultThumbnail(mention);

    if (!attachments.value.length && thumbnail) {
      // This is a single video post and the thumbnail image will be handled in the videos property
      if ("video_url" in mention && mention.video_url) return [];

      return [
        {
          type: "photo",
          thumbnail,
        },
      ];
    }

    if (attachments.value) {
      attachments.value.forEach((attachment) => {
        if ("media_type" in attachment && "image_url" in attachment) {
          if (
            ["graphimage", "image"].includes(
              attachment.media_type.toLowerCase()
            )
          ) {
            photoAttachments.push({
              type: "photo",
              thumbnail:
                isWebVideoFileUrl(attachment.image_url as string) &&
                "thumbnail_url" in mention
                  ? mention.thumbnail_url
                  : (attachment.image_url as string),
            });
          }
        }
      });
    }

    return photoAttachments;
  });

  const videos = computed(() => {
    const videoAttachments: VideoAttachment[] = [];

    if ("video_url" in mention && mention.video_url) {
      videoAttachments.push({
        type: "video",
        thumbnail: mention.image ?? "",
        url: mention.video_url ?? null,
      });
    }

    if (attachments.value?.length) {
      attachments.value.forEach((attachment) => {
        if (
          "media_type" in attachment &&
          "image_url" in attachment &&
          ["graphvideo", "video"].includes(attachment.media_type.toLowerCase())
        ) {
          const imageUrl = attachment.image_url as string;
          const isVideoFile = isWebVideoFileUrl(imageUrl);

          const thumbnailUrl =
            isVideoFile && "thumbnail_url" in mention && mention.thumbnail_url
              ? mention.thumbnail_url
              : imageUrl;

          const videoUrl = isVideoFile ? imageUrl : (mention.url ?? "");

          videoAttachments.push({
            type: "video",
            thumbnail: thumbnailUrl,
            url: videoUrl,
          });
        }
      });
    }

    return videoAttachments;
  });

  return {
    attachments,
    hasAttachments,
    photos,
    videos,
  };
}
